import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from "../../constants/config";
import ImageNotAvailable from "../../assets/Images/image_not_available.png";
import { useSelector, useDispatch } from 'react-redux';
import { deletePainting, moveToSold, moveToAvailable } from '../../store/actions/paintingsActions';

const PaintingCard = ({ painting }) => {
    const dispatch = useDispatch();
    const { admin } = useSelector(state => state.adminState);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionError, setActionError] = useState('');


    useEffect(() => {
        if (!showDeleteModal) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowDeleteModal(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden'; // блокируем скролл

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'auto'; // возвращаем
        };
    }, [showDeleteModal]);

    const getImageSrc = (img) => {
        if (!img) return ImageNotAvailable;

        const imageValue =
            typeof img === 'string'
                ? img
                : img?.url || '';

        if (!imageValue) return ImageNotAvailable;

        if (imageValue.startsWith('http')) {
            if (imageValue.includes('res.cloudinary.com')) {
                return imageValue.replace('/upload/', '/upload/f_auto,q_auto,w_900/');
            }
            return imageValue;
        }

        return `${apiUrl}/${imageValue}`;
    };

    const firstImage =
        painting.images?.length
            ? getImageSrc(painting.images[0])
            : ImageNotAvailable;

    const handleDelete = async () => {
        setActionError('');
        try {
            await dispatch(deletePainting(painting._id)).unwrap();
            setShowDeleteModal(false);
        } catch (err) {
            setShowDeleteModal(false);
            setActionError(typeof err === 'string' ? err : 'Failed to delete painting.');
        }
    };

    const handleMoveToSold = async () => {
        setActionError('');
        try {
            await dispatch(moveToSold(painting._id)).unwrap();
        } catch (err) {
            setActionError(typeof err === 'string' ? err : 'Failed to update painting.');
        }
    };

    const handleMoveToAvailable = async () => {
        setActionError('');
        try {
            await dispatch(moveToAvailable(painting._id)).unwrap();
        } catch (err) {
            setActionError(typeof err === 'string' ? err : 'Failed to update painting.');
        }
    };

    return (
        <div className="flex flex-col h-full relative">
            <Link
                to={`/painting/${painting._id}`}
                className="block overflow-hidden"
                title={painting.name}
            >
                <div className="relative">
                    <img
                        src={firstImage}
                        alt={painting.name}
                        className="w-full h-[420px] md:h-[460px] object-contain transition-transform duration-500 hover:scale-[1.02]"
                    />
                </div>
            </Link>

            <div className="text-center pt-4">
                <p className="text-sm md:text-base uppercase tracking-[0.08em] text-gray-700">
                    {painting.name}
                </p>

                {!painting.published && (
                    <p className="text-xs uppercase tracking-[0.12em] text-gray-500 mt-2">
                        Sold
                    </p>
                )}
            </div>

            {admin && (
                <div className="mt-4 flex justify-center gap-2 flex-wrap">
                    {painting.published ? (
                        <button
                            type="button"
                            onClick={handleMoveToSold}
                            className="text-[11px] uppercase tracking-[0.12em] border border-gray-200 px-4 py-2 text-gray-600 hover:border-gray-400 hover:text-black transition"
                        >
                            Mark as Sold
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleMoveToAvailable}
                            className="text-[11px] uppercase tracking-[0.12em] border border-gray-200 px-4 py-2 text-gray-600 hover:border-gray-400 hover:text-black transition"
                        >
                            Mark as Available
                        </button>
                    )}

                    <Link
                        to={`/admin/edit/${painting._id}`}
                        className="text-[11px] uppercase tracking-[0.12em] border border-gray-200 px-4 py-2 text-gray-600 hover:border-gray-400 hover:text-black transition"
                    >
                        Edit
                    </Link>

                    <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="text-[11px] uppercase tracking-[0.12em] border border-gray-200 px-4 py-2 text-gray-600 hover:border-red-400 hover:text-red-600 transition"
                    >
                        Delete
                    </button>
                </div>
            )}

            {admin && actionError && (
                <p className="mt-2 text-center text-[11px] uppercase tracking-[0.12em] text-red-600">
                    {actionError}
                </p>
            )}

            {showDeleteModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 animate-fadeIn"
                    onClick={() => setShowDeleteModal(false)} // клик по фону
                >
                    <div
                        className="w-full max-w-md bg-white border border-gray-200 p-6 text-center shadow-xl animate-fadeIn"
                        onClick={(e) => e.stopPropagation()} // ❗ чтобы клик внутри не закрывал
                    >
                        <h3 className="text-sm uppercase tracking-[0.2em] text-gray-800 mb-4">
                            Delete Painting
                        </h3>

                        <p className="text-sm text-gray-600 leading-relaxed mb-6">
                            Are you sure you want to delete <span className="italic">{painting.name}</span>?
                        </p>

                        <div className="flex justify-center gap-3 flex-wrap">
                            <button
                                type="button"
                                onClick={() => setShowDeleteModal(false)}
                                className="text-[11px] uppercase tracking-[0.12em] border border-gray-200 px-4 py-2 text-gray-600 hover:border-gray-400 hover:text-black transition"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleDelete}
                                className="text-[11px] uppercase tracking-[0.12em] border border-red-300 px-4 py-2 text-red-600 hover:bg-red-600 hover:text-white transition"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaintingCard;